import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, filter, Subscription} from 'rxjs';
import {ActivatedRoute, ParamMap, Params, Router} from '@angular/router';
import {map} from 'rxjs/operators';
import {AuthUntilUserRoleService} from '../../core/authentication/auth-until-user-role.service';
import {LearnerRankingResponseDto} from '../../../../../micromate-learn-lib/src/lib/types/LearnerRankingResponseDto';
import {RankingHelper} from '../../../../../micromate-learn-lib/src/lib/components/ranking/rankingHelper';
import {ScoreboardData} from '../../../../../micromate-learn-lib/src/lib/types/ScoreboardData';
import {RankingPeriod} from '../../../../../micromate-learn-lib/src/lib/types/RankingPeriod';
import {LearnerPointsResponseDto} from '../../../../../micromate-learn-lib/src/lib/types/LearnerPointsResponseDto';
import {
    ScoreboardRessource
} from '../../../../../micromate-learn-lib/src/lib/services/rest/scoreboard-ressource.service';
import {EventRecorderService} from '../../../../../micromate-learn-lib/src/lib/services/event/event-recorder.service';
import {
    TabRankingChangedPeriodType
} from '../../../../../micromate-learn-lib/src/lib/services/event/TabRankingChangedPeriodType';
import {TabEventType} from '../../../../../micromate-learn-lib/src/lib/services/event/TabEventType';
import {format} from 'date-fns';
import {ErrorNotificationService} from '../../core/error-notification.service';
import {
    ShowRankingOrganizationFeatureState
} from '../../../../../micromate-learn-lib/src/lib/services/ShowRankingOrganizationFeatureState';

@Component({
    selector: 'mm-learning-scoreboard',
    templateUrl: './learning-scoreboard.component.html',
    styleUrls: ['./learning-scoreboard.component.scss'],
    standalone: false
})
export class LearningScoreboardComponent implements OnInit, OnDestroy {
    public scoreboardData: BehaviorSubject<ScoreboardData | undefined> = new BehaviorSubject<ScoreboardData | undefined>(undefined);
    public scoreboardData$ = this.scoreboardData.pipe(filter((value): value is ScoreboardData => value !== undefined));
    public organizationExistPeriod = true;
    public rankingNotActiveInLearnerOrganization: ShowRankingOrganizationFeatureState = ShowRankingOrganizationFeatureState.Off;
    public learnerId: string | undefined;
    public activePeriod: RankingPeriod | undefined;
    public learnerPoints: LearnerPointsResponseDto | undefined;
    public scoreboard: LearnerRankingResponseDto | undefined;
    public paramsSubscription: Subscription | undefined;
    public params!: ParamMap;
    public openInConversation: boolean = false;

    constructor(private activatedRoute: ActivatedRoute,
                private scoreboardService: ScoreboardRessource,
                private authUntilUserRoleService: AuthUntilUserRoleService,
                private router: Router,
                private eventRecorderService: EventRecorderService,
                public errorNotificationService: ErrorNotificationService) {
    }

    public ngOnInit(): void {
        this.paramsSubscription = this.activatedRoute.queryParamMap.pipe(
            map(params => {
                this.params = params;
                this.learnerId = this.params.get('learnerid') ?? undefined;
                this.openInConversation = params.get('openInConversation') === 'true';
            }))
            .subscribe(async () => {
                await this.getScoreboard();
            });
    }

    private async getScoreboard(): Promise<void> {
        if (this.learnerPoints !== undefined) {
            await this.subscribeOnActivePeriod();
            await this.recordTabRankingChangedEvent();
        } else {
            if (this.learnerId === undefined) {
                await this.subscribeOnActivePeriod();
                await this.recordTabOpenedEvent();
            } else {
                const isAdmin = await this.authUntilUserRoleService.assertAdmin().toPromise();
                if (isAdmin !== true) {

                    void this.router.navigate(['/learn']);
                } else {
                    await this.subscribeOnActivePeriod(this.learnerId);
                }
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.paramsSubscription !== undefined) {
            this.paramsSubscription.unsubscribe();
        }
    }

    private async subscribeOnActivePeriod(learnerId?: string): Promise<void> {
        this.rankingNotActiveInLearnerOrganization = (await this.scoreboardService.getRankingActivatedStatusForLearn(learnerId)).rankingActiveState;
        const scoreboardData: ScoreboardData = {
            rankingNotActiveInLearnerOrganization: this.rankingNotActiveInLearnerOrganization,
            learnerId: this.learnerId
        };
        if (this.rankingNotActiveInLearnerOrganization !== ShowRankingOrganizationFeatureState.Off) {
            const learnerPointsPromise = this.scoreboardService.getLearnerPointsForLearn(learnerId);
            this.learnerPoints = await learnerPointsPromise;
            scoreboardData.learnerPoints = this.learnerPoints;

            if (this.rankingNotActiveInLearnerOrganization === ShowRankingOrganizationFeatureState.On) {
                const activePeriodInfo = this.params.get('period') ?? undefined;
                this.activePeriod = activePeriodInfo === undefined ? 'current-month' : activePeriodInfo as RankingPeriod;
                const scoreboardHelper = new RankingHelper();
                const period = scoreboardHelper.getPeriod(this.activePeriod);
                const date = format(scoreboardHelper.getPeriodDate(this.activePeriod), 'yyyy-MM-dd');
                this.scoreboard = await this.scoreboardService.getScoreboardForLearn(period, date, learnerId);
                this.organizationExistPeriod = this.scoreboard !== undefined;

                scoreboardData.activePeriod = this.activePeriod;
                scoreboardData.organizationExistPeriod = this.organizationExistPeriod;
                scoreboardData.scoreboard = this.scoreboard;
            }
        }
        this.scoreboardData.next(scoreboardData);
    }

    private async recordTabRankingChangedEvent(): Promise<void> {
        const activePeriodInfo = this.params.get('period') as TabRankingChangedPeriodType;
        await this.eventRecorderService.recordTabRankingChangedEventForLearn(this.learnerId, TabEventType.TabRankingChanged, activePeriodInfo);
    }

    private async recordTabOpenedEvent(): Promise<void> {
        const tabOpenEventPayload = this.eventRecorderService.createTabOpenEventPayloadForLearn(this.openInConversation, this.rankingNotActiveInLearnerOrganization !== ShowRankingOrganizationFeatureState.Off);
        await this.eventRecorderService.recordTabOpenedEventForLearn(undefined, TabEventType.TabRankingOpened, tabOpenEventPayload);
    }

    public periodSelected(period: RankingPeriod): void {
        const queryParams: Params = this.learnerId === undefined ? {period} : {learnerid: this.learnerId, period};
        void this.router.navigate(['learn/rangliste'], {
            queryParams,
            queryParamsHandling: 'merge'
        });
    }
}
